import { api } from './api';

export interface SubmitTradeRequestPayload {
  trade_type: 'buy' | 'sell' | string;
  requested_usd_amount: number;
  requested_crypto_amount?: number | null;
  business_name?: string;
  crypto_type: string;
  receiving_wallet_address?: string;
  memo_id?: string;
  customer_notes?: string;
  risk_acknowledged: boolean;
  confirmation_checked: boolean;
}

export interface SubmitTradeRequestResponse {
  id?: string;
  error?: string;
}

export interface TradeRequestDetails {
  id: string;
  status: string;
  business_name?: string;
  crypto_type?: string;
  receiving_wallet_address?: string;
  customer_notes?: string;
  transaction_hash?: string;
  requested_crypto_amount?: number;
  requested_usd_amount?: number;
  created_at?: string;
  executed_at?: string;
  quote_price_per_coin?: number;
  quote_amount_usd?: number;
  quote_status?: string;
  quote_crypto_amount?: number;
  quote_fee_amount?: number;
  quote_accepted_at?: string;
  quote_id?: string;
}

export interface GetTradeRequestsParams {
  status?: string;
  search?: string;
  page?: number;
  page_size?: number;
}

export interface GetTradeRequestsResponse {
  count: number;
  next?: string | null;
  previous?: string | null;
  items: TradeRequestDetails[];
}

export interface UpdateQuoteStatusPayload {
  quote_id: string;
  accepted: boolean;
}

export interface UpdateQuoteStatusResponse {
  id: string;
  trade_request_id: string;
  amount_usd: number;
  crypto_type: string;
  fee_percentage: number;
  crypto_amount: number;
  price_per_coin: number;
  fee_amount: number;
  wallet_address: string;
}

export const tradeRequestApi = {
  /**
   * List trade requests with optional filtering
   * @param params Optional query params for pagination and filtering
   * @returns A paginated list of trade requests
   */
  listTradeRequests: async (params: GetTradeRequestsParams = {}): Promise<GetTradeRequestsResponse> => {
    const queryParams: Record<string, string | number> = {};

    Object.entries(params).forEach(([key, value]) => {
      if (value === null || value === '') {
        return;
      }
      queryParams[key] = value;
    });

    const { data } = await api.get<GetTradeRequestsResponse>('/api/trade-requests', {
      params: queryParams,
    });

    return data;
  },

  /**
   * Submit a new trade request
   * @param requestData The trade request data
   * @returns A promise with the submission response
   */
  submitTradeRequest: async (requestData: SubmitTradeRequestPayload): Promise<SubmitTradeRequestResponse> => {
    try {
      const payload = Object.entries(requestData).reduce((acc: any, [key, value]) => {
        if (value !== undefined) {
          acc[key] = value;
        }
        return acc;
      }, {});

      const { data } = await api.post('/api/trade-requests/', payload);

      return data;
    } catch (error: any) {
      return {
        error:
          error.response?.data?.error ||
          'Trade request submission failed. Please check your information and try again.',
      };
    }
  },

  /**
   * Accept or reject a trade quote
   */
  updateQuoteStatus: async (payload: UpdateQuoteStatusPayload): Promise<UpdateQuoteStatusResponse> => {
    const { data } = await api.put<UpdateQuoteStatusResponse>('/api/quotes/status', payload);
    return data;
  },
};

export const convertRequestTradeFormToApiFormat = (formData: any): SubmitTradeRequestPayload => {
  const tradeType = 'buy' as const;

  const cryptoType = formData.transactionInfo?.cryptoType || 'BTC';
  const requestedUsdAmount = parseFloat(formData.transactionInfo?.purchaseAmount || '0');
  const businessName = formData.transactionInfo?.registeredBusinessName;
  const receivingWalletAddress = formData.transactionInfo?.receivingWalletAddress;
  const memoId = formData.transactionInfo?.memoId;
  const customerNotes = formData.transactionInfo?.specialNotes;
  const riskAcknowledged = !!formData.confirmationInfo?.riskAcknowledged;
  const confirmationChecked = !!formData.confirmationInfo?.confirmationChecked;

  /* eslint-disable camelcase */
  const payload: SubmitTradeRequestPayload = {
    trade_type: tradeType,
    requested_usd_amount: requestedUsdAmount,
    business_name: businessName,
    crypto_type: cryptoType,
    receiving_wallet_address: receivingWalletAddress,
    memo_id: memoId,
    customer_notes: customerNotes,
    risk_acknowledged: riskAcknowledged,
    confirmation_checked: confirmationChecked,
  };
  /* eslint-enable camelcase */
  return payload;
};
